import * as React from 'react';

import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';

import type { FieldError } from 'react-hook-form';
import { gray80, gray100, red50, gray60 } from '../../../constants/colors';
import { formLabelWrapper } from '../../../constants/location';

interface State {
  amount: string;
  password: string;
  weight: string;
  weightRange: string;
  showPassword: boolean;
}

type OutlinedInputProps = React.ComponentProps<typeof OutlinedInput>;

type Props = OutlinedInputProps & {
  error: boolean;
  formMb?: string | undefined;
  helperText: string | FieldError | undefined;
  labelText: string;
  requiredText?: string;
  width?: string;
};

const PasswordTextField = React.forwardRef((props: Props, ref): React.ReactElement => {
  const { error, formMb, helperText, labelText, requiredText, width, ...rest } = props;

  const [values, setValues] = React.useState<State>({
    amount: '',
    password: '',
    showPassword: false,
    weight: '',
    weightRange: '',
  });

  const handleClickShowPassword = (): void => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
  };

  function inputBorderFocusColor(): string {
    return error ? red50 : gray100;
  }

  function inputBorderColor(): string {
    return error ? red50 : gray60;
  }

  const styles = {
    formLabelWrapper,
    helper: {
      color: `${red50} !important`,
      '@media screen and (max-width:480px)': {
        marginLeft: 0,
        color: `${red50} !important`,
      },
    },
    icon: { color: gray80 },
    input: {
      '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: gray80,
      },
      '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: inputBorderColor(),
      },
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: inputBorderFocusColor(),
        borderWidth: '1px',
      },
      '&.MuiOutlinedInput-root:hover': {
        borderColor: inputBorderFocusColor(),
        borderWidth: '1px',
      },
      '@media screen and (max-width:600px)': {
        width: '100%',
      },
      height: '51px',
      width: width ?? '400px',
    },
    label: { fontSize: '14px' },
    required: {
      color: red50,
      fontSize: '14px',
      marginLeft: '6px',
    },
    formMb: { marginBottom: formMb ?? '24px' },
  };

  const textfieldHelper = (): string => {
    return String(helperText);
  };

  return (
    <Typography sx={styles.formMb} component="div">
      <Typography align="left" sx={styles.formLabelWrapper} component="div">
        <Typography color={gray80} sx={styles.label} component="span">
          {labelText}
        </Typography>
        {requiredText && (
          <Typography component="span" sx={styles.required}>
            {requiredText}
          </Typography>
        )}
      </Typography>
      <OutlinedInput
        {...rest}
        autoComplete="false"
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {values.showPassword ? (
                <VisibilityOutlined sx={styles.icon} />
              ) : (
                <VisibilityOffOutlined sx={styles.icon} />
              )}
            </IconButton>
          </InputAdornment>
        }
        inputRef={ref}
        sx={styles.input}
        type={values.showPassword ? 'text' : 'password'}
        value={rest.value ?? ''}
      />
      <FormHelperText sx={styles.helper} error={error}>
        {error && textfieldHelper()}
      </FormHelperText>
    </Typography>
  );
});
PasswordTextField.displayName = 'PasswordTextFiled';
export default PasswordTextField;
